import { lazy, Suspense, useEffect } from "react";
import {
  Outlet,
  Route,
  BrowserRouter as Router,
  Routes,
} from "react-router-dom";

// Keep core/shared utilities eagerly loaded
import GlobalLoading from "./components/GlobalLoading";
import LoadingScreen from "./components/LoadingScreen/LoadingScreen";
import ProtectedRoute from "./components/ProtectedRoute";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import EventRegistrationv2 from "./pages/EventDashboard/EventRegistrationv2";
import MemberForAllMember from "./pages/MemberForAllMember";
import RecentActivityPage from "./pages/RecentActivityPage";
import CookiePolicy from "./pages/public/CookiePolicy";
import Legal from "./pages/public/Legal";
import Media from "./pages/public/Media";
import News from "./pages/public/News";
import NewsDetails from "./pages/public/NewsDetails";
import PrivacyPolicy from "./pages/public/PrivacyPolicy";
import Publication from "./pages/public/Publication";
import PublicationDetails from "./pages/public/PublicationDetails";
const NoticSectionAllData = lazy(
  () => import("./components/NoticSectionAllData"),
);

// Lazy-load layouts
const Layout = lazy(() => import("./components/Layout/Layout"));
const DashboardLayout = lazy(() => import("./components/Layout/Layout3"));

// Lazy-load pages
const Dashboard = lazy(() => import("./homepageV1/Dashboard/Dashboard"));
const LandingPage = lazy(() => import("./homepageV1/LandingPage"));
const MemberSectionAllData = lazy(
  () => import("./homepageV1/MemberSectionAllData"),
);
const AddMember = lazy(() => import("./pages/AddMember/AddMember"));
const MemberReview = lazy(() => import("./pages/AddMember/MemberReview"));
const UpdateMemberProfile = lazy(
  () => import("./pages/AddMember/UpdateMemberProfile"),
);

const AdminDashboard = lazy(
  () => import("./pages/AdminDashboard/AdminDashboard"),
);
const AdminMembershipPaymentPage = lazy(
  () => import("./pages/AdminMembershipPaymentPage"),
);
const AdminProfile = lazy(() => import("./pages/AdminProfile/AdminProfile"));

const CreateAdditionalFee = lazy(
  () => import("./pages/CreateAdditionalFee/CreateAdditionalFee"),
);

const AddEventPage = lazy(() => import("./pages/EventDashboard/AddEventPage"));
const EventIndexPage = lazy(
  () => import("./pages/EventDashboard/EventIndexPage"),
);
const EventList = lazy(() => import("./pages/EventDashboard/EventList"));
const EventRegistration = lazy(
  () => import("./pages/EventDashboard/EventRegistration"),
);
const EventRegistrationsList = lazy(
  () => import("./pages/EventDashboard/EventRegistrationsList"),
);
const MemberEventDetailsPage = lazy(
  () => import("./pages/EventDashboard/MemberEventDetailsPage"),
);

const EventsPage = lazy(() => import("./pages/EventsPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));

const MemberDashboardv2 = lazy(
  () => import("./pages/MemberDashboard/MemberDashboardv2"),
);
const MemberEvent = lazy(() => import("./pages/MemberEvent/MemberEvent"));
const EventDetails = lazy(() => import("./pages/EventDetails/EventDetails"));
const MemberList = lazy(() => import("./pages/MemberList/MemberList"));
const MemberManagementDashboard = lazy(
  () => import("./pages/MemberManagementDashboard/MemberManagementDashboard"),
);

const AdminMemberView = lazy(
  () => import("./pages/MemberProfile/AdminMemberView"),
);
const MemberProfile = lazy(() => import("./pages/MemberProfile/MemberProfile"));
const MembershipFeesPage = lazy(() => import("./pages/MembershipFeesPage"));
const MembershipTypeList = lazy(
  () => import("./pages/MembershipTypeList/MembershipTypeList"),
);

const OfflinePayment = lazy(() => import("./pages/Payment/OfflinePayment"));
const OnlinePayment = lazy(() => import("./pages/Payment/OnlinePayment"));
const PaymentSuccessfull = lazy(
  () => import("./pages/Payment/PaymentSuccessfull"),
);
const RecurringPaymentCallback = lazy(
  () => import("./pages/Payment/RecurringPaymentCallback"),
);
const PaymentConfirmationPage = lazy(
  () => import("./pages/PaymentConfirmationpage"),
);

const ProfileProcessing = lazy(
  () => import("./pages/ProfileProcessing/ProfileProcessing"),
);
const NotificationsPage = lazy(() => import("./pages/NotificationsPage"));
const SiteMap = lazy(() => import("./components/SiteMap"));

// Public pages
const CsrAndWelfare = lazy(() => import("./pages/public/CsrAndWelfare"));
const Faq = lazy(() => import("./pages/public/Faq"));
const ContactUs = lazy(() => import("./pages/public/ContactUs"));
const IBAExciss = lazy(() => import("./pages/public/IBAExciss"));
const Gallery = lazy(() => import("./pages/public/Gallery"));
const PublicationAndMedia = lazy(
  () => import("./pages/public/PeblicationAndMedia"),
);
const Service = lazy(() => import("./pages/public/Service/Service"));
const HowToMakePayment = lazy(() => import("./pages/HowToMakePayment"));
const ServiceItem = lazy(() => import("./pages/public/Service/ServiceItem"));
const AffiliatedClubs = lazy(
  () => import("./pages/public/TheClub/AffiliatedClubs"),
);
const AlumniAssociation = lazy(
  () => import("./pages/public/TheClub/AlumniAssociation"),
);
const BoardOfDirectors = lazy(
  () => import("./pages/public/TheClub/BoardOfDirectors"),
);
const ChairpersonWords = lazy(
  () => import("./pages/public/TheClub/ChairpersonWords"),
);
const FormerBods = lazy(() => import("./pages/public/TheClub/FormerBods"));
const MoaAndLaws = lazy(() => import("./pages/public/TheClub/MoaAndLaws"));
const SubCommittees = lazy(
  () => import("./pages/public/TheClub/SubCommittees"),
);
const SubCommitteeDetails = lazy(
  () => import("./pages/public/TheClub/SubCommitteeDetails"),
);

function AppContent() {
  const { isLoading } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <Routes>
      <Route path="login-1" element={<GlobalLoading />} />
      <Route
        path="/"
        element={
          <Layout>
            <Outlet />
          </Layout>
        }
      >
        <Route index element={<LandingPage />} />
        <Route path="member/all" element={<MemberSectionAllData />} />
        <Route path="login" element={<LoginPage />} />

        <Route path="login-2" element={<Dashboard />} />
        <Route path="faq" element={<Faq />} />
        <Route path="contact-us" element={<ContactUs />} />
        <Route
          path="IBAXIS"
          element={
            <ProtectedRoute requiredRoles={["MEMBER", "ADMIN"]}>
              <Publication />
            </ProtectedRoute>
          }
        />
        <Route
          path="IBAXIS/:id"
          element={
            <ProtectedRoute requiredRoles={["MEMBER", "ADMIN"]}>
              <PublicationDetails />
            </ProtectedRoute>
          }
        />

        <Route path="service" element={<Service />} />
        <Route path="service/:id" element={<ServiceItem />} />
        <Route
          path="the-club/chairperson-words"
          element={<ChairpersonWords />}
        />
        <Route
          path="the-club/board-of-directors"
          element={<BoardOfDirectors />}
        />
        <Route path="the-club/former-bods" element={<FormerBods />} />
        <Route path="the-club/sub-committees" element={<SubCommittees />} />
        <Route
          path="the-club/sub-committees/:id"
          element={<SubCommitteeDetails />}
        />
        <Route
          path="the-club/moa-laws"
          element={
            <ProtectedRoute requiredRoles={["MEMBER", "ADMIN"]}>
              <MoaAndLaws />
            </ProtectedRoute>
          }
        />
        <Route path="the-club/affiliated-clubs" element={<AffiliatedClubs />} />
        <Route
          path="the-club/alumni-association"
          element={<AlumniAssociation />}
        />
        <Route path="csr-welfare" element={<CsrAndWelfare />} />
        <Route path="news" element={<News />} />
        <Route path="news/:id" element={<NewsDetails />} />
        <Route path="Media" element={<Media />} />
        <Route path="publication" element={<Publication />} />
        <Route path="publication/:id" element={<PublicationDetails />} />
        <Route path="gallery" element={<Gallery />} />
        <Route path="event" element={<EventsPage />} />
        <Route path="notice" element={<NoticSectionAllData />} />
        <Route path="sitemap" element={<SiteMap />} />
        <Route path="privacy-policy" element={<PrivacyPolicy />} />
        <Route path="cookie-policy" element={<CookiePolicy />} />
        <Route path="legal" element={<Legal />} />
        <Route path="event-details/:eventId" element={<EventDetails />} />
      </Route>
      <Route
        path="admin"
        element={
          <ProtectedRoute requiredRoles={["ADMIN"]}>
            <MemberManagementDashboard />
          </ProtectedRoute>
        }
        // element={<MemberManagementDashboard />}
      >
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="dashboard/activities" element={<RecentActivityPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        {/* <Route path="dashboard-page" element={<AdminDashboard />} /> */}{" "}
        <Route path="members" element={<MemberList />} />
        <Route path="members/add/:memberId" element={<AddMember />} />
        <Route path="members/view/:memberId" element={<AdminMemberView />} />
        <Route path="members/add/:memberId/review" element={<MemberReview />} />
        <Route path="membership-types" element={<MembershipTypeList />} />
        <Route path="profile" element={<AdminProfile />} />
        <Route path="profile/:memberId" element={<ProfileProcessing />} />
        <Route path="membership-fee" element={<AdminMembershipPaymentPage />} />
        <Route
          path="membership-fee/offline/:paymentId"
          element={<OfflinePayment />}
        />
        <Route
          path="membership-fee/online/:paymentId"
          element={<OnlinePayment />}
        />
        <Route path="events" element={<EventIndexPage />} />
        <Route path="events/add/:id" element={<AddEventPage />} />
        <Route
          path="create-additional-fee/:paymentId"
          element={<CreateAdditionalFee />}
        />
        <Route path="events-registration/:id" element={<EventRegistration />} />
        <Route path="events-list-2" element={<EventList />} />
        <Route
          path="events/:id/registrations"
          element={<EventRegistrationsList />}
        />
        <Route path="events/:id/details" element={<MemberEventDetailsPage />} />
        <Route
          path="membership-fee/payment-confirmation"
          element={<PaymentConfirmationPage />}
        />
      </Route>
      <Route
        path="/member"
        element={
          <ProtectedRoute requiredRoles={["MEMBER"]}>
            <MemberManagementDashboard />
          </ProtectedRoute>
        }
        // element={<MemberManagementDashboard />}
      >
        <Route
          path="membership-fee/payment-confirmation"
          element={<PaymentConfirmationPage />}
        />
        <Route path="dashboard" element={<MemberDashboardv2 />} />
        <Route path="dashboard/activities" element={<RecentActivityPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="profile" element={<MemberProfile />} />
        <Route path="tutorial" element={<HowToMakePayment />} />
        <Route path="profile/:memberId" element={<UpdateMemberProfile />} />
        {/* <Route path="dashboard-page" element={<AdminDashboard />} /> */}

        <Route path="member-list" element={<MemberForAllMember />} />
        <Route path="add-member/:memberId" element={<AddMember />} />
        <Route path="add-member/:memberId/review" element={<MemberReview />} />
        <Route
          path="membership-fee/offline/:paymentId"
          element={<OfflinePayment />}
        />
        <Route path="the-club/affiliated-clubs" element={<AffiliatedClubs />} />
        <Route
          path="the-club/alumni-association"
          element={<AlumniAssociation />}
        />
        <Route path="csr-welfare" element={<CsrAndWelfare />} />
        <Route path="publication-media" element={<PublicationAndMedia />} />
        <Route path="gallery" element={<Gallery />} />
      </Route>
      <Route
        path="admin"
        element={
          <ProtectedRoute requiredRoles={["ADMIN"]}>
            <MemberManagementDashboard />
          </ProtectedRoute>
        }
        // element={<MemberManagementDashboard />}
      >
        <Route path="dashboard" element={<AdminDashboard />} />
        {/* <Route path="dashboard-page" element={<AdminDashboard />} /> */}{" "}
        <Route path="members" element={<MemberList />} />
        <Route path="members/add/:memberId" element={<AddMember />} />
        <Route path="members/view/:memberId" element={<AdminMemberView />} />
        <Route path="members/add/:memberId/review" element={<MemberReview />} />
        <Route path="membership-types" element={<MembershipTypeList />} />
        <Route path="profile" element={<AdminProfile />} />
        <Route path="profile/:memberId" element={<ProfileProcessing />} />
        <Route path="membership-fee" element={<AdminMembershipPaymentPage />} />
        <Route
          path="membership-fee/offline/:paymentId"
          element={<OfflinePayment />}
        />
        <Route
          path="membership-fee/online/:paymentId"
          element={<OnlinePayment />}
        />
        <Route path="events" element={<EventIndexPage />} />
        <Route path="events/add/:id" element={<AddEventPage />} />
        <Route
          path="create-additional-fee/:paymentId"
          element={<CreateAdditionalFee />}
        />
        <Route path="events-registration/:id" element={<EventRegistration />} />
        <Route path="events-list-2" element={<EventList />} />
        <Route
          path="events/:eventId/registration"
          element={<EventRegistrationv2 />}
        />
        <Route
          path="events/:id/registrations"
          element={<EventRegistrationsList />}
        />
        <Route path="events/:id/details" element={<MemberEventDetailsPage />} />
      </Route>
      <Route
        path="/member"
        element={
          <ProtectedRoute requiredRoles={["MEMBER"]}>
            <MemberManagementDashboard />
          </ProtectedRoute>
        }
        // element={<MemberManagementDashboard />}
      >
        <Route
          path="membership-fee/payment-confirmation"
          element={<PaymentConfirmationPage />}
        />
        <Route path="dashboard" element={<MemberDashboardv2 />} />
        <Route path="profile" element={<MemberProfile />} />
        <Route path="profile/:memberId" element={<UpdateMemberProfile />} />
        {/* <Route path="dashboard-page" element={<AdminDashboard />} /> */}

        <Route path="member-list" element={<MemberList />} />
        <Route path="add-member/:memberId" element={<AddMember />} />
        <Route path="add-member/:memberId/review" element={<MemberReview />} />
        <Route
          path="membership-fee/offline/:paymentId"
          element={<OfflinePayment />}
        />
        <Route
          path="membership-fee/online/:paymentId"
          element={<OnlinePayment />}
        />
        <Route path="event-fee/online/:paymentId" element={<OnlinePayment />} />
        <Route path="payment-successfull" element={<PaymentSuccessfull />} />
        <Route
          path="payment/recurring/callback"
          element={<RecurringPaymentCallback />}
        />
        <Route path="membership-fee" element={<MembershipFeesPage />} />
        <Route path="events" element={<MemberEvent />} />
        <Route path="event-details/:eventId" element={<EventDetails />} />
        <Route path="events/add/:id" element={<AddEventPage />} />
        <Route
          path="events/:eventId/registration"
          element={<EventRegistrationv2 />}
        />
        <Route path="events-list-2" element={<EventList />} />
        <Route
          path="events/:id/registrations"
          element={<EventRegistrationsList />}
        />
        <Route path="events/:id/details" element={<MemberEventDetailsPage />} />
      </Route>
      <Route
        path="/membership-fees"
        element={
          <DashboardLayout>
            <MembershipFeesPage />
          </DashboardLayout>
        }
      />
      <Route
        path="/payment-confirmation"
        element={<PaymentConfirmationPage />}
      />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    localStorage.setItem("theme", "light");
  }, []);

  // Show disclaimer only on landing routes and react to route changes
  // const RouteAwareDisclaimer = () => {
  //   const location = useLocation();
  //   const [open, setOpen] = useState(true);
  //   const isLanding =
  //     location.pathname === "/" || location.pathname === "/login-1";
  //   if (!isLanding) return null;
  //   return <DisclaimerModal isOpen={open} onClose={() => setOpen(false)} />;
  // };
  return (
    <Suspense fallback={<GlobalLoading />}>
      <Router>
        <AuthProvider>
          <NotificationProvider>
            {/* <RouteAwareDisclaimer /> */}
            <AppContent />
          </NotificationProvider>
        </AuthProvider>
      </Router>
    </Suspense>
  );
}

export default App;
